import React from 'react';
import {TouchableOpacity, ViewStyle, TextStyle, StyleProp, Platform, StyleSheet, View, Keyboard} from 'react-native';

import {useTheme} from '../../theme';
import {IconProps} from '../svg/model';
import {Text} from '../text';
import {useButtonStyles} from './styles';

export type ButtonType = 'primary' | 'secondary' | 'transparent';

type Props = {
  id: string;
  onPress?: () => void;
  onPressIn?: () => void;
  type?: ButtonType;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  block?: boolean;
  activeOpacity?: number;
  icon?: React.FC<IconProps>;
  iconProps?: IconProps;
  iconStyles?: StyleProp<ViewStyle>;
  iconPosition?: 'left' | 'right';
  text?: string;
};

export const Button: React.FC<Props> = ({
  disabled,
  onPress,
  text,
  onPressIn,
  type,
  style,
  textStyle,
  block = false,
  activeOpacity = 0.5,
  icon: Icon,
  iconProps = {width: 16, height: 16},
  iconPosition = 'right',
  iconStyles,
  id,
}) => {
  const btnType: ButtonType = type || 'primary';

  const theme = useTheme();
  const styles = useButtonStyles(block);

  const buttonStyles = [styles.button, theme.button[btnType], style];
  const textStyles = [styles.text, theme.buttonText[btnType], textStyle];

  const btnStyle = disabled ? [...buttonStyles, theme.button[btnType].disabled] : buttonStyles;

  const txtStyle = StyleSheet.flatten(disabled ? [...textStyles, theme.buttonText.disabled[btnType]] : textStyles);

  const dataSetButtonAttrs = {
    dataSet: {
      class: 'button',
      'class-m': `button--${btnType}${disabled ? ' button-disabled' : ''}`,
    },
  };

  const dataSetButtonTextAttrs = {
    dataSet: {
      class: 'button-text',
    },
  };

  const {color} = StyleSheet.flatten(txtStyle) as {color: string};

  return (
    <TouchableOpacity
      style={btnStyle}
      {...dataSetButtonAttrs}
      onPress={() => {
        Keyboard.dismiss();
        if (onPress !== undefined) {
          onPress();
        }
      }}
      onPressIn={onPressIn}
      accessibilityRole='button'
      activeOpacity={Platform.OS === 'web' ? 1 : activeOpacity}
      disabled={disabled}
      testID={`btn-${id}`}>
      {Icon && iconPosition === 'left' ? (
        <View style={[styles.iconLeft, iconStyles]}>
          <Icon {...iconProps} color={color} />
        </View>
      ) : null}
      <Text testID={id ? `button-text-${id}-id` : 'no-text-id'} {...dataSetButtonTextAttrs} style={txtStyle}>
        {text}
      </Text>
      {Icon && iconPosition === 'right' ? (
        <View style={[styles.iconRight, iconStyles]}>
          <Icon {...iconProps} color={color} />
        </View>
      ) : null}
    </TouchableOpacity>
  );
};
